import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';

import { Field, Fields, FieldArray, reduxForm, SubmissionError } from 'redux-form';

import TextField from 'components/fields/TextField';
import DatepickerField from 'components/fields/DatepickerField';
import MultiselectAutosuggestField from 'components/fields/MultiselectAutosuggestField';
import FormError from 'components/common/FormError';
import IconLink from 'components/icons/IconLink';

import {
  createReferralSource,
  updateReferralSource,
  createReferralSourceContact,
  updateReferralSourceContact,
  deleteReferralSourceContact,
  addReferralSourceResource,
  removeReferralSourceResource,
} from 'redux/modules/referralSources';
import {
  requestFetchPrescribers,
  requestSearchPrescribers,
  addPrescriberResource,
  removePrescriberResource,
} from 'redux/modules/prescribers';
import {
  requestFetchSalesRepresentatives,
  requestSearchSalesRepresentatives,
} from 'redux/modules/salesRepresentatives';
import { accountLoginFail } from 'redux/modules/account';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';
import objectDiff from 'utils/objectDiff';

import dateFormat from 'config/dateFormat';

import ReferralSourceContactFieldArray from './ReferralSourceContactFieldArray';

const styles = () => ({
  grid: {
    marginTop: 10,
    '&:first-child': {
      marginTop: 0,
    },
  },
  iconLink: {
    margin: '0 8px -7px 0',
  },
});

function onSubmit(values, dispatch, props) {
  const normalizeValues = (formValues) => {
    const normalizedValues = Object.assign({}, formValues);

    if (normalizedValues['Cooperate from'] !== undefined) {
      if (normalizedValues['Cooperate from'] === '') {
        normalizedValues['Cooperate from'] = null;
      } else {
        normalizedValues['Cooperate from'] =
            moment(normalizedValues['Cooperate from'], dateFormat).format(dateFormat);
      }
    }

    if (normalizedValues.Email !== undefined
      && normalizedValues.Email === '') {
      normalizedValues.Email = null;
    }

    delete normalizedValues['Referral Source Contact'];
    delete normalizedValues['Sales Representative'];
    delete normalizedValues.Prescriber;

    return normalizedValues;
  };

  if (props.ID) {
    if (props.pristine) {
      throw new SubmissionError({
        _error: 'Nothing to submit',
      });
    }
    const diffValues = objectDiff(values, props.initialValues);
    const updatedContacts = diffValues['Referral Source Contact']
      ? values['Referral Source Contact'].filter((item) => {
        const itemInitialValue = props.initialValues['Referral Source Contact'].find(initialItem =>
          initialItem.ID === item.ID);
        if (!itemInitialValue) {
          return false;
        }
        const itemDiffValues = objectDiff(item, itemInitialValue);
        return Object.keys(itemDiffValues).length > 0;
      }).filter(item => item).map(item => ({
        ...item,
        ...item.Email === '' ? {
          Email: null,
        } : {},
        ...item['Contact Name'] === '' ? {
          'Contact Name': null,
        } : {},
        ...item.Fax === '' ? {
          Fax: null,
        } : {},
      })) : [];
    const addedContacts = diffValues['Referral Source Contact']
      ? values['Referral Source Contact'].filter(item => !item.ID)
      : [];
    const removedContacts = diffValues['Referral Source Contact']
      ? props.initialValues['Referral Source Contact']
        .filter(initialItem => values['Referral Source Contact'].filter(item => item.ID === initialItem.ID).length === 0)
      : [];
    const addedSales = values['Sales Representative'].filter(item =>
      props.initialValues['Sales Representative'].filter(initialItem =>
        initialItem.value === item.value).length === 0);
    const removedSales = props.initialValues['Sales Representative'].filter(initialItem =>
      values['Sales Representative'].filter(item =>
        item.value === initialItem.value).length === 0);
    const addedPrescriber = values.Prescriber.filter(item =>
      props.initialValues.Prescriber.filter(initialItem =>
        initialItem.value === item.value).length === 0);
    const removedPrescriber = props.initialValues.Prescriber.filter(initialItem =>
      values.Prescriber.filter(item =>
        item.value === initialItem.value).length === 0);
    return new Promise((resolve, reject) => {
      if (updatedContacts.length) {
        dispatch(updateReferralSourceContact({
          values: {
            'Referral Source Contact': updatedContacts,
          },
          resolve,
          reject,
        }));
      } else {
        resolve();
      }
    })
      .then(() => new Promise((resolve, reject) => {
        if (addedContacts.length) {
          dispatch(createReferralSourceContact({
            values: {
              'Referral Source Contact': addedContacts,
              'Referral Source ID': props.ID,
            },
            resolve,
            reject,
          }));
        } else {
          resolve();
        }
      }))
      .then(() => new Promise((resolve, reject) => {
        if (removedContacts.length) {
          dispatch(deleteReferralSourceContact({
            values: {
              'Referral Source Contact': removedContacts,
            },
            resolve,
            reject,
          }));
        } else {
          resolve();
        }
      }))
      .then(() => new Promise((resolve, reject) => {
        if (Object.keys(normalizeValues(diffValues)).length > 0) {
          dispatch(updateReferralSource({
            values: {
              ID: props.ID,
              ...normalizeValues(diffValues),
            },
            resolve,
            reject,
          }));
        } else {
          resolve();
        }
      }))
      .then(() => new Promise((resolve, reject) => {
        if (addedSales.length > 0) {
          dispatch(addReferralSourceResource({
            values: {
              ID: props.ID,
              'Sales Representative ID': addedSales.map(item => item.value),
            },
            resolve,
            reject,
          }));
        } else {
          resolve();
        }
      }))
      .then(() => new Promise((resolve, reject) => {
        if (removedSales.length > 0) {
          dispatch(removeReferralSourceResource({
            values: {
              ID: props.ID,
              'Sales Representative ID': removedSales.map(item => item.value),
            },
            resolve,
            reject,
          }));
        } else {
          resolve();
        }
      }))
      .then(() => new Promise((resolve, reject) => {
        if (addedPrescriber.length > 0) {
          Promise.all(
            addedPrescriber.map(item =>
              new Promise((allItemResolve, allItemReject) => {
                dispatch(addPrescriberResource({
                  values: {
                    ID: item.value,
                    'Referral Source ID': [ props.ID ],
                  },
                  resolve: allItemResolve,
                  reject: allItemReject,
                }))
              })
            )).then(() => {
            resolve();
          });      
        } else {
          resolve();
        }
      }))
      .then(() => new Promise((resolve, reject) => {
        if (removedPrescriber.length > 0) {
          Promise.all(
            removedPrescriber.map(item =>
              new Promise((allItemResolve, allItemReject) => {
                dispatch(removePrescriberResource({
                  values: {
                    ID: item.value,
                    'Referral Source ID': [ props.ID ],
                  },
                  resolve: allItemResolve,
                  reject: allItemReject,
                }))
              })
            )).then(() => {
            resolve();
          });      
        } else {
          resolve();
        }
      }))
      .then(res => res,
        (err) => {
          if (err.error) {
            throw new SubmissionError({ _error: err.error });
          }
          throw new SubmissionError(err);
        });
  }
  return new Promise((resolve, reject) => {
    dispatch(createReferralSource({
      values: {
        ...normalizeValues(values),
        ...values['Referral Source Contact'] ? {
          'Referral Source Contact': values['Referral Source Contact'],
        } : {},
        ...values['Sales Representative'] ? {
          'Sales Representative ID': values['Sales Representative'].map(item => item.value),
        } : {},
      },
      resolve,
      reject,
    }));
  })
  .then((createValues) => new Promise((resolve, reject) => {
    if (values.Prescriber && values.Prescriber.length > 0) {
      Promise.all(
        values.Prescriber.map(item =>
          new Promise((allItemResolve, allItemReject) => {
            dispatch(addPrescriberResource({
              values: {
                ID: item.value,
                'Referral Source ID': [ createValues.ID ],
              },
              resolve: allItemResolve,
              reject: allItemReject,
            }))
          })
        )).then(() => {
        resolve(createValues);
      });      
    } else {
      resolve(createValues);
    }
  }))
  .then(res => res,
    (err) => {
      if (err.error) {
        throw new SubmissionError({ _error: err.error });
      }
      throw new SubmissionError(err);
    });
}

function validateForm(values) {
  const errors = {};

  if (values['Cooperate from']
  && moment(values['Cooperate from'], dateFormat).isAfter(moment().endOf('day'))) {
    errors['Cooperate from'] = 'Start date can not be in future';
  }

  return errors;
}

const ReferralSourceCreateEditForm = ({
  handleSubmit,
  error,
  className,
  classes,
  array,
  actions,
}) => (
  <form
    noValidate
    autoComplete="off"
    onSubmit={handleSubmit}
    className={className}
  >
    {error &&
      <Grid
        className={classes.grid}
        container
        spacing={24}
      >
        <Grid
          item
          xs={12}
        >
          <FormError>
            {error}
          </FormError>
        </Grid>
      </Grid>
    }
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid
        item
        xs={12}
      >
        <Field
          id="Name of source"
          name="Name of source"
          label="Name of Source"
          component={TextField}
          validate={[validate.required]}
          errorPlacement="bottom"
          fullWidth
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      spacing={0}
    >
      <Grid
        item
        xs={7}
      >
        <Field
          id="Cooperate from"
          name="Cooperate from"
          label="Start Date"
          component={DatepickerField}
          validate={[validate.date]}
          normalize={normalize.date}
          fullWidth
          minDate="01/01/1900"
          maxDate={moment().format(dateFormat)}
          dateFormat={dateFormat}
          placeholder="mm/dd/yyyy"
          endIcon="start"
          errorPlacement="bottom"
          datepickerPlacement="bottom-left"
        />
      </Grid>
    </Grid>
    <FieldArray
      name="Referral Source Contact"
      component={ReferralSourceContactFieldArray}
    />
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid item xs={12}>
        <Fields
          label={(
            <Fragment>
              <IconLink className={classes.iconLink} />
              Linked Prescriber
            </Fragment>
          )}
          names={[
            'Prescriber Name',
            'Prescriber',
          ]}
          autosuggestFieldName="Prescriber Name"
          fieldArrayName="Prescriber"
          placeholder="-Select Prescriber-"
          component={MultiselectAutosuggestField}
          renderSuggestionContent={suggestion =>
            `${suggestion['First Name']} ${suggestion['Last Name']}`}
          renderInputContent={suggestion =>
            `${suggestion['First Name']} ${suggestion['Last Name']}`}
          onSuggestionSelected={(suggestion) => {
            array.push('Prescriber', {
              value: suggestion.ID,
              label: `${suggestion['First Name']} ${suggestion['Last Name']}`,
            });
            return '';
          }}
          fetchSuggestions={({ value, limit, offset, fieldArrayValue }) => (
            value.length > 0 || (fieldArrayValue && fieldArrayValue.length > 0)
              ? requestSearchPrescribers({
                filters: [
                  ...value.length > 0
                    ? [{
                      name: 'Full Name',
                      operation: 'startswith',
                      val: value,
                      case_sensitivity: false,
                    }] : [],
                  ...fieldArrayValue && fieldArrayValue.length > 0
                    ? fieldArrayValue.map(arrayItem => ({
                      name: 'ID',
                      operation: 'ne',
                      val: arrayItem.value,
                    }))
                    : [],
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
              : requestFetchPrescribers({
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
          )}
          keepOpenAfterSelection
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid item xs={12}>
        <Fields
          label={(
            <Fragment>
              <IconLink className={classes.iconLink} />
              Linked Sales
            </Fragment>
          )}
          names={[
            'Sales Name',
            'Sales Representative',
          ]}
          autosuggestFieldName="Sales Name"
          fieldArrayName="Sales Representative"
          placeholder="-Select Sales-"
          component={MultiselectAutosuggestField}
          renderSuggestionContent={suggestion =>
            `${suggestion['First Name']} ${suggestion['Last Name']}`}
          renderInputContent={suggestion =>
            `${suggestion['First Name']} ${suggestion['Last Name']}`}
          onSuggestionSelected={(suggestion) => {
            array.push('Sales Representative', {
              value: suggestion.ID,
              label: `${suggestion['First Name']} ${suggestion['Last Name']}`,
            });
            return '';
          }}
          fetchSuggestions={({ value, limit, offset, fieldArrayValue }) => (
            value.length > 0 || (fieldArrayValue && fieldArrayValue.length > 0)
              ? requestSearchSalesRepresentatives({
                filters: [
                  ...value.length > 0
                    ? [{
                      name: 'Full Name',
                      operation: 'startswith',
                      val: value,
                      case_sensitivity: false,
                    }] : [],
                  ...fieldArrayValue && fieldArrayValue.length > 0
                    ? fieldArrayValue.map(arrayItem => ({
                      name: 'ID',
                      operation: 'ne',
                      val: arrayItem.value,
                    }))
                    : [],
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
              : requestFetchSalesRepresentatives({
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
          )}
          keepOpenAfterSelection
        />
      </Grid>
    </Grid>
  </form>
);

ReferralSourceCreateEditForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  error: PropTypes.string,
  className: PropTypes.string,
  actions: PropTypes.shape({
    accountLoginFail: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    grid: PropTypes.string.isRequired,
  }).isRequired,
};

ReferralSourceCreateEditForm.defaultProps = {
  error: null,
  className: '',
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ accountLoginFail }, dispatch),
});

export default connect(null, mapDispatchToProps)(reduxForm({
  onSubmit,
  validate: validateForm,
  initialValues: {
    'Cooperate from': moment().format(dateFormat),
    'Referral Source Contact': [{
      Phone: '',
    }],
  },
})(withStyles(styles)(ReferralSourceCreateEditForm)));



// WEBPACK FOOTER //
// ./src/components/settings/ReferralSourceCreateEditDialog/ReferralSourceCreateEditForm.js